/* Verbatim-behavior port of the two localStorage-backed UI globals:
 *   - ACT_STATUS (service/review/index.html ~1272-1274): per-action-plan-row
 *     open/in-progress/done status, keyed 'fb-actions-{slugify(brand)}'.
 *   - FLAGGED (~1803-1805): URLs flagged for mapping from the ★ IMPACT
 *     result popover, keyed 'fb-mapflags-{slugify(brand)}'.
 * Both were bare `let` globals + load/save function pairs reading/writing
 * the module-level variable. Reshaped as tiny Store wrappers (rule (a) —
 * no closed-over globals in module scope) with `load(brand)`/`save(brand)`
 * replacing loadActStatus/saveActStatus and loadFlags/saveFlags; brand is
 * passed explicitly (the original re-derived it from the STATE global via
 * `slugify(STATE.brand)` every call) since this module doesn't hold a
 * reference to payloadStore (avoids a state.ts <-> ui.ts import cycle).
 * try/catch-swallow behavior (bad JSON, quota/private-mode errors) is
 * preserved exactly — same as/fallback to {} or [].
 */
import { slugify } from '../lib/format.ts';
import { createStore, type Store } from './store.ts';

export interface ActStatusStore extends Store<Record<string, string>> {
  load(brand: string): void;
  save(brand: string): void;
}

function actStatusKey(brand: string): string {
  return 'fb-actions-' + slugify(brand);
}

function createActStatusStore(): ActStatusStore {
  const store = createStore<Record<string, string>>({});
  return {
    ...store,
    load(brand: string) {
      try { store.set(JSON.parse(localStorage.getItem(actStatusKey(brand)) || '{}')); }
      catch (e) { store.set({}); }
    },
    save(brand: string) {
      try { localStorage.setItem(actStatusKey(brand), JSON.stringify(store.get())); }
      catch (e) { /* quota / private-mode — same silent no-op as saveActStatus */ }
    },
  };
}

export const actStatusStore: ActStatusStore = createActStatusStore();

export interface FlaggedEntry {
  url: string;
  /** null when neither the click context nor domainOf(url) had a name */
  title?: string | null;
  at?: string;
}

export interface FlaggedStore extends Store<FlaggedEntry[]> {
  load(brand: string): void;
  save(brand: string): void;
}

function flaggedKey(brand: string): string {
  return 'fb-mapflags-' + slugify(brand);
}

function createFlaggedStore(): FlaggedStore {
  const store = createStore<FlaggedEntry[]>([]);
  return {
    ...store,
    load(brand: string) {
      try { store.set(JSON.parse(localStorage.getItem(flaggedKey(brand)) || '[]')); }
      catch (e) { store.set([]); }
    },
    save(brand: string) {
      try { localStorage.setItem(flaggedKey(brand), JSON.stringify(store.get())); }
      catch (e) { /* quota / private-mode — same silent no-op as saveFlags */ }
    },
  };
}

export const flaggedStore: FlaggedStore = createFlaggedStore();

/* The AM workspace layer (branch am-workspace, `const WS = {...}` at
 * ~3655) lands in hub/src/state/workspace.ts (F2d). Every
 * `typeof WS !== 'undefined' && WS.available === true` guard in ported code
 * (views/exec.ts, views/conflicts.ts, views/actions.ts) calls wsAvailable()
 * — kept exported from THIS module (not moved into state/workspace.ts) so
 * every existing `import { actStatusStore, wsAvailable } from
 * '../state/ui.ts'` line stays byte-identical (F2d brief: "no other edits"
 * to already-ported views).
 *
 * wsAvailable() can't import the real `WS` singleton directly — workspace.ts
 * already imports actStatusStore from this module (for WS.migrateOnce()), so
 * a direct import back would be circular at the value level. Instead
 * workspace.ts registers a live availability check once, at module-eval
 * time, via registerWsAvailability(); this module just holds the callback
 * indirection. Defaults to "false", matching the pre-F2d behavior for any
 * caller evaluated before the workspace module registers itself (should
 * never happen in practice — main.ts's dependency graph pulls in
 * state/workspace.ts before any view renders — but a safe default either
 * way). */
let _wsAvailabilityCheck: () => boolean = () => false;

export function registerWsAvailability(check: () => boolean): void {
  _wsAvailabilityCheck = check;
}

export function wsAvailable(): boolean {
  return _wsAvailabilityCheck();
}
